import { useEffect } from "react"
import { Link, useLocation } from "react-router-dom"
import { useQuery, useQueryClient } from "@tanstack/react-query"
import { CheckSquare } from "lucide-react"
import { api } from "@/lib/api"
import { defaultTasksListParams, tasksListQuery } from "@/lib/tasks-query"
import { useAuth } from "@/hooks/useAuth"
import { useWorkspace } from "@/hooks/use-workspace"
import { cn } from "@/lib/utils"
import { isAdmin as userIsAdmin } from "@/lib/roles"

/**
 * The header's "Tasks" control: a straight link to /tasks with the open-task
 * count. It used to open a quick-tasks popover (five tasks + quick add); Justin
 * asked for the click to go directly to the page instead (2026-08-26).
 */
export function TasksHeaderLink() {
  const { user } = useAuth()
  const location = useLocation()
  const queryClient = useQueryClient()

  // This component only mounts behind the auth gate, so reaching here means the
  // session is settled. /tasks is the app's busiest page and the one users
  // without a licence are redirected to — so fetch the list now rather than
  // when someone finally clicks through. prefetchQuery honours staleTime, so
  // navigating in later costs nothing more.
  const userId = user?.id
  const isAdmin = userIsAdmin(user)
  useEffect(() => {
    if (!userId) return
    queryClient.prefetchQuery(tasksListQuery(defaultTasksListParams(userId, isAdmin)))
  }, [userId, isAdmin, queryClient])

  // Deliberately not the tasks page's cache entry: that query asks for every
  // task matching its filters and this one only needs the total, so a shared
  // key would hand one of them the other's answer. The "tasks" prefix still
  // means the existing invalidateQueries({ queryKey: ["tasks"] }) calls
  // refresh this too.
  const { data: tasksPage } = useQuery({
    queryKey: ["tasks", "topbar", user?.id],
    queryFn: () => api.tasks.listPage({ status: "open", involving: user?.id, per_page: 1 }),
    enabled: !!user?.id,
    refetchInterval: 60_000,
  })

  // The server's count of everything matching, not the length of this page.
  const totalCount = tasksPage?.meta.total ?? 0
  const isActive = location.pathname === "/tasks" || location.pathname.startsWith("/tasks/")

  // From another workspace this is a workspace switch, so it plays the same
  // overlay as the sidebar switcher. Modified clicks (new tab) stay plain links.
  const { current, available, switchTo } = useWorkspace()
  const switchesWorkspace = current !== "tasks" && available.includes("tasks")

  return (
    <Link
      to="/tasks"
      onClick={(e) => {
        if (!switchesWorkspace || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return
        e.preventDefault()
        switchTo("tasks", "/tasks")
      }}
      aria-current={isActive ? "page" : undefined}
      className={cn(
        "flex h-8 items-center gap-1.5 rounded-md px-2.5 text-sm transition-colors hover:bg-accent hover:text-foreground",
        isActive ? "text-foreground" : "text-muted-foreground"
      )}
    >
      <CheckSquare className="h-4 w-4" />
      <span className="hidden md:inline">Tasks</span>
      {totalCount > 0 && (
        <span className="flex h-5 min-w-5 items-center justify-center rounded-md bg-black/[0.06] px-1.5 text-[11px] font-medium tabular-nums text-foreground dark:bg-white/10">
          {totalCount}
        </span>
      )}
    </Link>
  )
}
